import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import SideNav from "../common/sidenav";
import TopNav from "../common/topnav";
import Loading from "../loading/loading";
import "../common/style.css";
import {
  uploadFile,
  createEvidence,
  getEvidenceById,
  evidenceFile,
} from "../http/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function EvidenceForm() {
  const { batchId, evidenceId } = useParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    localStorage.getItem("sb|sidebar-toggle") === "true"
  );

  const [formData, setFormData] = useState({
    id: 0,
    date: "",
    photo1: "",
    photo2: "",
    photo3: "",
    photo4: "",
    video: "",
  });

  const [previewUrls, setPreviewUrls] = useState({});
  const [loading, setLoading] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    localStorage.setItem("sb|sidebar-toggle", !isSidebarOpen);
  };

  const handleChange = (e) => {
    const { name, files, value } = e.target;
    const file = files ? files[0] : value;

    setFormData((prev) => ({
      ...prev,
      [name]: file,
    }));

    if (file instanceof File) {
      setPreviewUrls((prev) => ({
        ...prev,
        [name]: URL.createObjectURL(file),
      }));
    } else {
      setPreviewUrls((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const resizeImage = (file, maxDim = 800) => {
    // ... (rest of the function is unchanged)
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith("image/")) return resolve(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxDim) {
              height *= maxDim / width;
              width = maxDim;
            }
          } else {
            if (height > maxDim) {
              width *= maxDim / height;
              height = maxDim;
            }
          }

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile);
            },
            file.type,
            0.9
          );
        };
        img.src = e.target.result;
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  const createThumbnail = (file, maxDim = 150) => {
    // ... (rest of the function is unchanged)
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith("image/")) return resolve(null);
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxDim) {
              height *= maxDim / width;
              width = maxDim;
            }
          } else {
            if (height > maxDim) {
              width *= maxDim / height;
              height = maxDim;
            }
          }

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              const tnFile = new File([blob], "tn_" + file.name, {
                type: file.type,
              });
              resolve(tnFile);
            },
            file.type,
            0.9
          );
        };
        img.src = e.target.result;
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  const uploadAndGetKey = async (file, prefix) => {
    if (!file) return null;

    let originalKey = null;
    if (file instanceof File) {
      let fileToUpload = file;
      if (file.type.startsWith("image/")) {
        fileToUpload = await resizeImage(file, 800);
      }

      const keyName = `${prefix}_${Date.now()}_${file.name}`;
      const fd = new FormData();
      fd.append("file", fileToUpload);
      fd.append("keyName", keyName);
      const res = await uploadFile(fd);
      if (res.status === 200 || res.status === 202) originalKey = keyName;

      const tnFile = await createThumbnail(file);
      if (tnFile) {
        const tnKeyName = `tn_${keyName}`;
        const tnFd = new FormData();
        tnFd.append("file", tnFile);
        tnFd.append("keyName", tnKeyName);
        await uploadFile(tnFd);
      }
      return originalKey;
    }
    return file.name || file;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const keys = await Promise.all([
        uploadAndGetKey(formData.photo1, `photo1_${batchId}`),
        uploadAndGetKey(formData.photo2, `photo2_${batchId}`),
        uploadAndGetKey(formData.photo3, `photo3_${batchId}`),
        uploadAndGetKey(formData.photo4, `photo4_${batchId}`),
        uploadAndGetKey(formData.video, `video_${batchId}`),
      ]);

      const evidenceDTO = {
        id: formData.id,
        date: formData.date,
        batchId: batchId,
        photo1: keys[0] || "",
        photo2: keys[1] || "",
        photo3: keys[2] || "",
        photo4: keys[3] || "",
        video1: keys[4] || "",
      };

      const response = await createEvidence(evidenceDTO);

      if (response.status === 200 || response.status === 201) {
        toast.success("Evidence saved successfully!");
        setFormData({
          id: 0,
          date: "",
          photo1: "",
          photo2: "",
          photo3: "",
          photo4: "",
          video: "",
        });
        setPreviewUrls({});
      } else {
        throw new Error("Failed to save evidence");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error uploading evidence.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!evidenceId) return;

    const fetchEvidence = async () => {
      setLoading(true);
      try {
        const res = await getEvidenceById(evidenceId);
        if (res.status === 200) {
          const data = res.data;
          setFormData({
            id: data.id || 0,
            date: data.date || "",
            photo1: data.photo1 || "",
            photo2: data.photo2 || "",
            photo3: data.photo3 || "",
            photo4: data.photo4 || "",
            video: data.video1 || "",
          });

          const urls = {};
          if (data.photo1) {
            const fileRes = await evidenceFile(data.photo1);
            urls.photo1 = URL.createObjectURL(new Blob([fileRes.data], { type: "image/jpeg" }));
          }
          if (data.photo2) {
            const fileRes = await evidenceFile(data.photo2);
            urls.photo2 = URL.createObjectURL(new Blob([fileRes.data], { type: "image/jpeg" }));
          }
          if (data.photo3) {
            const fileRes = await evidenceFile(data.photo3);
            urls.photo3 = URL.createObjectURL(new Blob([fileRes.data], { type: "image/jpeg" }));
          }
          if (data.photo4) {
            const fileRes = await evidenceFile(data.photo4);
            urls.photo4 = URL.createObjectURL(new Blob([fileRes.data], { type: "image/jpeg" }));
          }
          if (data.video1) {
            const fileRes = await evidenceFile(data.video1);
            urls.video = URL.createObjectURL(new Blob([fileRes.data], { type: "video/mp4" }));
          }
          setPreviewUrls(urls);
        } else {
          toast.error("Failed to fetch evidence data");
        }
      } catch (err) {
        console.error(err);
        toast.error("Error fetching evidence data");
      } finally {
        setLoading(false);
      }
    };
    fetchEvidence();
  }, [evidenceId]);

  const handleDownload = async (fileName) => {
    if (!fileName) return;
    try {
      const response = await evidenceFile(fileName);
      const blob = new Blob([response.data], {
        type: "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error("Failed to download file");
    }
  };

  const renderFilePreview = (fileData, label) => {
    const isVideo = label.toLowerCase().includes("video");
    const isNewFile = fileData instanceof File;
    const url = isNewFile ? URL.createObjectURL(fileData) : previewUrls[label.toLowerCase().replace(" ", "")];

    if (!url) return null;

    return (
      <div style={{ marginTop: "10px" }}>
        {isVideo ? (
          <video width="240" height="160" controls src={url}>
            Your browser does not support the video tag.
          </video>
        ) : (
          <img
            src={url}
            alt={label}
            style={{ width: "120px", height: "120px", objectFit: "cover" }}
          />
        )}
        <br />
        <button
          type="button"
          className="btn btn-sm btn-outline-primary mt-2"
          onClick={() => handleDownload(fileData instanceof File ? fileData.name : fileData)}
        >
          Download {label}
        </button>
      </div>
    );
  };

  return (
    <div className="EvidenceForm">
      {loading && <Loading />}
      <div className={`sb-nav-fixed ${isSidebarOpen ? "sb-sidenav-toggled" : ""}`}>
        <TopNav toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
        <div id="layoutSidenav">
          <SideNav isSidebarOpen={isSidebarOpen} />
          <div id="layoutSidenav_content">
            <main>
              <div className="container-fluid px-4">
                <h4 className="mt-4">
                  {evidenceId ? "Edit Evidence" : "Evidence"} - Batch {batchId}
                </h4>

                <div className="card shadow-sm border-0">
                  <div className="card-header">
                    <h5 className="mb-0">
                      {evidenceId ? "Edit Evidence" : "Add Evidence"}
                    </h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleSubmit}>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">Date</label>
                          <input
                            type="date"
                            name="date"
                            className="form-control"
                            value={formData.date}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="row">
                        {[1, 2, 3, 4].map((num) => (
                          <div className="col-md-6 mb-3" key={num}>
                            <label className="form-label">
                              Photo Evidence {num}
                            </label>
                            <input
                              type="file"
                              name={`photo${num}`}
                              accept="image/*"
                              className="form-control"
                              onChange={handleChange}
                            />
                            {renderFilePreview(
                              formData[`photo${num}`],
                              `Photo ${num}`
                            )}
                          </div>
                        ))}

                        <div className="col-md-6 mb-3">
                          <label className="form-label">Video Evidence</label>
                          <input
                            type="file"
                            name="video"
                            accept="video/*"
                            className="form-control"
                            onChange={handleChange}
                          />
                          {renderFilePreview(formData.video, "Video")}
                        </div>
                      </div>

                      <div className="alert alert-warning small">
                        <strong>Note:</strong> Images must be under <b>1 MB</b>,
                        video under <b>5 MB</b>.
                      </div>

                      <div className="text-end">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading
                            ? "Uploading..."
                            : evidenceId
                            ? "Update Evidence"
                            : "Add Evidence"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
    </div>
  );
}